/**
 * Article detail — 單篇代表文章完整資料（prototype 假資料）
 *
 * DEMO: 示意資料，正式資料待客戶/後端。均為通用化名（診所/醫師皆 ○○ 風格），非真實機構。
 * 對應資料模型：Article（詳情）+ Review（評價）。欄位定義見 docs/handoff/HANDOFF_BACKEND.md §5 資料模型。
 *
 * 用途：
 *   - article-detail.html 內文目前以靜態 markup 呈現（與此資料一致），
 *     此檔為「資料單一真相」雛形，供 article-detail.js 渲染評論列表 + 待接 CMS 時 fetch 來源。
 *   - 待接 backend：上線後改為依 slug fetch from CMS；dateLabel 由 date 於 render 時格式化。
 */

// TODO(backend): data 檔改依 slug/id fetch CMS 詳情填同 shape（renderer 不動），徽章值由後端權威驅動 — 見 docs/BACKEND_HANDOFF.md#detail-content-data
export const ARTICLE = {
  slug: 'hyaluronic-acid-evidence-grade',
  category: '證據科普',
  title: '玻尿酸填充：你需要知道的證據等級',
  publishedAt: '2026-03-10',
  publishedLabel: '2026.03.10',
  reviewedAt: '2026.03.08',
  expiresAt: '2026.09.08',
  version: 'v1.2',
  // 路徑用相對 './'：article-detail.html 為 root 頁，baseURI 安全、子路徑部署不 404（與 render-cards.js 同慣例）。
  heroImage: './images/articles/article-1.jpg',
  heroAlt: '醫師持注射器示意玻尿酸填充療程，桌面排列療程衛教資料',
};

/**
 * 讀者評價。rating 1~5 整數。
 * status：審核狀態，公開列表只顯示 'published'（render 端以 isEligible 過濾）。
 *   'published' 已通過審核、公開可見
 *   'pending'   送出後待審核，不公開
 *   'returned'  審核退回，不公開
 * 平台規則：評價需經審核後公開。前 3 則為策展（curated）已公開評價。
 */
export const REVIEWS = [
  {
    id: 'r-001',
    name: '陳○婷',
    rating: 5,
    date: '2026-03-12',
    dateLabel: '2026.03.12',
    status: 'published',
    body: '術前看完這篇對證據等級總算有概念，去諮詢時知道要請醫師出示原廠仿單與臨床資料，不再只聽口頭保證。',
  },
  {
    id: 'r-002',
    name: '林○宏',
    rating: 4,
    date: '2026-03-11',
    dateLabel: '2026.03.11',
    status: 'published',
    body: '內容紮實、來源都標得很清楚。唯一希望能多補充不同廠牌的維持時間比較，其餘都很受用。',
  },
  {
    id: 'r-003',
    name: '王○瑜',
    rating: 5,
    date: '2026-03-10',
    dateLabel: '2026.03.10',
    status: 'published',
    body: '「我們已知 / 尚未確定」這段很實在，沒有把療程講得百分百完美，反而讓我更願意相信平台。',
  },
];

/* ───────────────────────────────────────────────────────────────────────────
 * MOCK: 最大值驗證用，接 API 後移除
 * ───────────────────────────────────────────────────────────────────────────
 * 目的：程式化灌入大量評價，讓設計 / QA 看「最壞版面」（最多頁數、最長內文、
 *   字數落差、跨頁分頁邊界、空態以外的滿載態）。pageSize=5 → 約 99 頁。
 * 標記：本批 id 一律 'mock-NNN'；整段以本註解框界定，接後端時連同下方 buildMockReviews()
 *   呼叫一起刪除即可，curated 前 3 則與 REVIEW_SUMMARY 不受影響。
 * 過濾驗證：多數 status='published'，每隔固定間距夾入 1 則 'pending' / 'returned'，
 *   用來驗 isEligible 只放行 published（非 published 不得出現在公開列表 / 不計入頁數）。
 */
function buildMockReviews(count) {
  const surnames = ['李', '張', '黃', '吳', '劉', '蔡', '楊', '許', '鄭', '謝', '郭', '洪', '曾', '邱', '廖', '賴', '周', '徐', '蘇', '葉'];
  const givenMasked = ['○華', '○倫', '○芳', '○庭', '○儒', '○妍', '○彥', '○潔', '○豪', '○薇', '○哲', '○琪', '○翔', '○雯', '○ани']; // 末項刻意含非中字驗韌性
  const bodies = [
    '看完整篇條理很清楚，諮詢前先做了功課，現場溝通順暢很多。',
    '把證據等級拆成幾個層級講解，對沒有醫學背景的人也很好懂，受益良多，謝謝平台用心整理這些資料，讓人更安心做決定，也更知道該問醫師哪些關鍵問題，避免被話術帶著走，整體是很扎實的一篇衛教文。',
    '資訊密度高但不雜亂，重點都有標出來。',
    '原本對療程半信半疑，讀完比較知道怎麼判斷資訊真偽。',
    '希望未來能多一些不同部位的案例比較，這篇已經很完整了。',
    '把「我們已知 / 尚未確定」分開講這點很加分，誠實的內容反而更可信。',
    '文末的來源清單很實用，直接拿去跟醫師對照。',
    '排版舒服、讀起來不費力，重點段落一目了然，通勤路上很快就看完。',
  ];
  const out = [];
  for (let i = 0; i < count; i += 1) {
    const n = i + 1;
    const seq = String(n).padStart(3, '0');
    // status：每 17 則 1 pending、每 23 則 1 returned，其餘 published（多數公開）
    let status = 'published';
    if (n % 23 === 0) status = 'returned';
    else if (n % 17 === 0) status = 'pending';
    // rating 在 3~5 間擺盪（多為高分，偶有 3 分）
    const rating = n % 11 === 0 ? 3 : (n % 5 === 0 ? 4 : 5);
    // 日期：自 2026-03-09 起每則往前一天，產生變化與排序差異
    const d = new Date(Date.UTC(2026, 2, 9));
    d.setUTCDate(d.getUTCDate() - i);
    const iso = d.toISOString().slice(0, 10);
    out.push({
      id: `mock-${seq}`,
      name: `${surnames[i % surnames.length]}${givenMasked[i % givenMasked.length]}`,
      rating,
      date: iso,
      dateLabel: iso.replace(/-/g, '.'),
      status,
      body: bodies[i % bodies.length],
    });
  }
  return out;
}

// MOCK: 最大值驗證用，接 API 後移除（約 495 則 → 約 99 頁 @ pageSize 5）
REVIEWS.push(...buildMockReviews(495));
/* ──────────────────────── MOCK 區結束 ─────────────────────── */

/** 評價統計：用於總星評呈現（prototype 取 curated 3 則 + mock published 平均，四捨五入到 0.1）。 */
export const REVIEW_SUMMARY = {
  average: 4.7,
  count: REVIEWS.filter((r) => r.status === 'published').length,
};
